import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../services/baseUrl";
import { UserContext } from "../hooks/UserContext";

const BASE_API =
  process.env.REACT_APP_API_BASE_URL ||
  api?.defaults?.baseURL ||
  "https://api.krayneagency.fr/api";
const API_ROOT = BASE_API.replace(/\/api\/?$/, "");

const buildImageUrl = (img) => {
  if (!img) return null;
  let path =
    typeof img === "object"
      ? (img.path_watermarked ?? img.path_image ?? img.image_url ?? "")
      : String(img);
  if (!path) return null;
  if (path.startsWith("http")) return path;
  path = path.replace(/^\/+/, "");
  if (path.startsWith("storage/")) path = path.slice(8);
  if (path.startsWith("api/images/")) path = path.slice(11);
  return `${API_ROOT}/api/images/${path}`;
};

/* ─── Image protégée (data URI) ─────────────────────────────────────────── */
function ProtectedImg({ src, alt, className, style }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!src) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setDataUrl(null);
    (async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(src, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        if (cancelled) return;
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!cancelled) {
            setDataUrl(reader.result);
            setStatus("ok");
          }
        };
        reader.readAsDataURL(blob);
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (status === "loading")
    return (
      <div
        className={`${className} bg-white/5 animate-pulse flex items-center justify-center`}
        style={style}
      >
        <span className="text-white/20 text-xs">…</span>
      </div>
    );
  if (status === "error" || !dataUrl)
    return (
      <div
        className={`${className} bg-white/5 flex items-center justify-center`}
        style={style}
      >
        <span className="text-white/30 text-xs">Image</span>
      </div>
    );
  return (
    <img
      src={dataUrl}
      alt={alt}
      className={className}
      style={style}
      draggable={false}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}

export default function RealisationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  /* ── Commentaires ── */
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({
    author_name: "",
    comment: "",
    rating: 5,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [commentRating, setCommentRating] = useState(5);

  /* Pré-remplir le nom avec le pseudo du compte */
  useEffect(() => {
    if (!user) return;
    const displayName =
      user.nickname ||
      `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim() ||
      user.email ||
      "";
    setCommentForm((f) => ({ ...f, author_name: displayName }));
  }, [user]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    api
      .get(`/realisations/${id}`)
      .then((res) => {
        const p = res.data?.data || res.data || {};
        const heroImg = p.main_image ? buildImageUrl(p.main_image) : null;
        const galleryPaths =
          Array.isArray(p.gallery) && p.gallery.length > 0
            ? p.gallery
            : Array.isArray(p.images)
              ? p.images.filter((x) => x !== p.main_image)
              : [];
        setProject({
          title: p.title ?? p.name ?? "",
          description: p.description ?? p.short_description ?? "",
          heroImage: heroImg,
          galleryImages: galleryPaths.map(buildImageUrl).filter(Boolean),
          category: p.category ?? null,
          tech_stack: Array.isArray(p.tech_stack) ? p.tech_stack : [],
          client_name: p.client_name ?? p.creator_name ?? null,
          project_url: p.project_url ?? p.project_link ?? p.link ?? null,
          completed_at: p.completed_at ?? null,
          is_featured: p.is_featured ?? false,
          id_user_client: p.id_user_client ?? null,
        });
      })
      .catch(() => setError("Projet introuvable."))
      .finally(() => setLoading(false));

    api
      .get(`/realisations/${id}/comments`)
      .then((res) => setComments(res.data?.data || []))
      .catch(() => {});
  }, [id]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentForm.author_name.trim() || !commentForm.comment.trim()) return;
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const res = await api.post(`/realisations/${id}/comments`, {
        ...commentForm,
        rating: commentRating,
      });
      setComments((prev) => [res.data.data, ...prev]);
      setCommentForm({ author_name: "", comment: "", rating: 5 });
      setCommentRating(5);
      setSubmitMsg("Merci pour votre avis !");
    } catch {
      setSubmitMsg("Une erreur s'est produite, veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07070a]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-fuchsia-500/30 border-t-fuchsia-500 animate-spin" />
          <span className="text-white/50 text-sm">Chargement du projet…</span>
        </div>
      </div>
    );

  if (error || !project)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07070a]">
        <div className="text-center">
          <p className="text-rose-400 text-lg font-bold mb-4">
            {error || "Projet introuvable."}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="text-white/50 hover:text-white text-sm transition-colors"
          >
            ← Retour
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#07070a] text-white">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <div className="relative h-[55vh] min-h-[340px] overflow-hidden">
        {project.heroImage ? (
          <img
            src={project.heroImage}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "brightness(0.45)" }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-900/40 via-indigo-900/30 to-[#07070a]" />
        )}
        {/* Gradient de fondu vers le bas */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#07070a] via-[#07070a]/40 to-transparent" />

        {/* Navigation */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 pt-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors backdrop-blur-sm bg-white/5 border border-white/10 px-4 py-2 rounded-full"
          >
            ← Retour
          </button>
          {project.is_featured && (
            <span className="px-3 py-1 rounded-full text-xs font-bold border bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30">
              ★ Mis en avant
            </span>
          )}
        </div>

        {/* Titre hero */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-10 max-w-5xl mx-auto">
          {project.category && (
            <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30 mb-3">
              {typeof project.category === "object"
                ? project.category?.name
                : project.category}
            </span>
          )}
          <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-tight">
            {project.title}
          </h1>
        </div>
      </div>

      {/* ── Contenu principal ─────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 pb-20 -mt-2">
        {/* Description + Dashboard (2 colonnes) */}
        <div className="grid md:grid-cols-[1.2fr_0.8fr] gap-6 mb-8">
          {/* Colonne gauche — description */}
          <div className="flex flex-col gap-5">
            <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-6">
              <p className="text-base text-gray-300 leading-relaxed">
                {project.description || "Aucune description disponible."}
              </p>
            </div>
            {project.tech_stack.length > 0 && (
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
                  Stack technique
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.tech_stack.map((t, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/15 text-indigo-300 border border-indigo-500/25"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {project.client_name && (
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center text-sm font-bold shrink-0">
                  {project.client_name[0]}
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-widest">
                    Client
                  </p>
                  <p className="text-sm font-semibold text-white">
                    {project.client_name}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Colonne droite — infos projet */}
          <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 flex flex-col gap-4 self-start">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">
              Infos du projet
            </p>

            {project.category && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">
                  Catégorie
                </p>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/20">
                  {typeof project.category === "object"
                    ? project.category?.name
                    : project.category}
                </span>
              </div>
            )}

            {project.completed_at && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">
                  Date de livraison
                </p>
                <p className="text-sm font-semibold text-white">
                  {new Date(project.completed_at).toLocaleDateString("fr-FR", {
                    year: "numeric",
                    month: "long",
                  })}
                </p>
              </div>
            )}

            {project.client_name && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">
                  Client
                </p>
                <p className="text-sm font-semibold text-white">
                  {project.client_name}
                </p>
              </div>
            )}

            {project.tech_stack.length > 0 && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-2">
                  Technologies
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {project.tech_stack.map((t, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 rounded text-[11px] font-medium bg-white/5 border border-white/10 text-gray-300"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {project.is_featured && (
              <div className="rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/20 p-3">
                <p className="text-xs font-bold text-fuchsia-300">
                  ★ Mis en avant
                </p>
                <p className="text-[11px] text-fuchsia-400/70 mt-0.5">
                  Réalisation mise en avant sur la page d'accueil
                </p>
              </div>
            )}

            {project.project_url && (
              <a
                href={project.project_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-white text-sm bg-gradient-to-r from-fuchsia-600 to-indigo-500 hover:from-fuchsia-500 hover:to-indigo-400 shadow-lg shadow-fuchsia-500/20 transition-all duration-200"
              >
                Voir le site →
              </a>
            )}
          </div>
        </div>

        {/* ── Galerie d'images ─────────────────────────────────────────── */}
        {project.galleryImages.length > 0 && (
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-4">
              Galerie du projet
            </p>
            <div
              className={`grid gap-3 ${project.galleryImages.length === 1 ? "grid-cols-1" : project.galleryImages.length === 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3"}`}
            >
              {project.galleryImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(img)}
                  className="group relative overflow-hidden rounded-2xl border border-white/8 aspect-video cursor-zoom-in"
                >
                  <ProtectedImg
                    src={img}
                    alt={`${project.title} ${i + 2}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold bg-black/50 px-3 py-1 rounded-full">
                      Agrandir
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Section commentaires ─────────────────────────────────────── */}
        <div className="mt-10">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-6">
            Avis & commentaires
          </p>

          {comments.length > 0 ? (
            <div className="flex flex-col gap-4 mb-8">
              {comments.map((c) => (
                <div
                  key={c.id_comment}
                  className="rounded-2xl border border-white/8 bg-white/[0.03] p-5"
                >
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center text-xs font-bold shrink-0">
                        {(c.author_name || "?")[0].toUpperCase()}
                      </div>
                      <p className="text-sm font-semibold text-white">
                        {c.author_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span
                            key={i}
                            className={`text-base leading-none ${i < c.rating ? "text-amber-400" : "text-white/15"}`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(c.created_at).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {c.comment}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 mb-8">
              Aucun avis pour le moment. Soyez le premier !
            </p>
          )}

          {/* Formulaire de commentaire — réservé au client propriétaire */}
          {user &&
          project?.id_user_client &&
          Number(user.id_user) === Number(project.id_user_client) ? (
            <form
              onSubmit={handleCommentSubmit}
              className="rounded-2xl border border-white/10 bg-black/30 backdrop-blur-sm p-6 flex flex-col gap-4"
            >
              <p className="text-sm font-bold text-white">Laisser un avis</p>

              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                  Note
                </label>
                <div className="flex gap-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCommentRating(i + 1)}
                      className={`text-2xl leading-none transition-all duration-100 cursor-pointer select-none ${
                        i < commentRating
                          ? "text-amber-400 scale-110"
                          : "text-white/20 hover:text-amber-400/60"
                      }`}
                    >
                      ★
                    </button>
                  ))}
                  <span className="ml-2 text-xs text-gray-500 self-center">
                    {commentRating} / 5
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5">
                  Votre nom
                </label>
                <input
                  type="text"
                  readOnly
                  value={commentForm.author_name}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 text-sm cursor-default select-none"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5">
                  Commentaire
                </label>
                <textarea
                  required
                  rows={4}
                  value={commentForm.comment}
                  onChange={(e) =>
                    setCommentForm((f) => ({ ...f, comment: e.target.value }))
                  }
                  maxLength={2000}
                  placeholder="Partagez votre expérience…"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/20 text-sm focus:outline-none focus:border-fuchsia-500/50 transition-colors resize-none"
                />
              </div>

              {submitMsg && (
                <p
                  className={`text-sm font-medium ${
                    submitMsg.startsWith("Merci")
                      ? "text-emerald-400"
                      : "text-rose-400"
                  }`}
                >
                  {submitMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="self-start px-6 py-2.5 rounded-xl font-bold text-white text-sm bg-gradient-to-r from-fuchsia-600 to-indigo-500 hover:from-fuchsia-500 hover:to-indigo-400 disabled:opacity-50 transition-all duration-200"
              >
                {submitting ? "Envoi…" : "Publier l'avis"}
              </button>
            </form>
          ) : project?.id_user_client ? (
            <div className="rounded-2xl border border-white/10 bg-black/30 backdrop-blur-sm p-6">
              <p className="text-sm text-gray-500 italic">
                Seul le client propriétaire de ce projet peut laisser un avis.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Lightbox ─────────────────────────────────────────────────── */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/60 hover:text-white text-2xl leading-none"
            onClick={() => setLightbox(null)}
          >
            ✕
          </button>
          <ProtectedImg
            src={lightbox}
            alt="Agrandie"
            className="max-w-full max-h-[90vh] rounded-2xl object-contain shadow-2xl"
            style={{ pointerEvents: "none" }}
          />
        </div>
      )}
    </div>
  );
}
